import { createContext, useContext, useEffect, useState, useCallback } from "react";
import { api } from "./api";

const AuthContext = createContext(null);

export const AuthProvider = ({ children }) => {
  const [user, setUser] = useState(() => {
    try {
      return JSON.parse(localStorage.getItem("bi_user") || "null");
    } catch {
      return null;
    }
  });
  const [photo, setPhoto] = useState(() => localStorage.getItem("bi_photo") || null);
  const [loading, setLoading] = useState(true);

  const fetchPhoto = useCallback(async () => {
    try {
      const { data } = await api.get("/auth/me/photo");
      if (data.photo) {
        setPhoto(data.photo);
        localStorage.setItem("bi_photo", data.photo);
      }
    } catch (e) {
      // photo isn't critical — log so missing/expired tokens are visible in devtools.
      console.debug("auth/me/photo skipped:", e?.response?.status || e?.message);
    }
  }, []);

  const refresh = useCallback(async () => {
    try {
      const { data } = await api.get("/auth/me");
      setUser(data);
      localStorage.setItem("bi_user", JSON.stringify(data));
      return data;
    } catch (e) {
      console.debug("auth/me refresh failed:", e?.response?.status || e?.message);
      return null;
    }
  }, []);

  useEffect(() => {
    const token = localStorage.getItem("bi_token");
    if (!token) {
      setLoading(false);
      return;
    }
    api
      .get("/auth/me")
      .then((r) => {
        setUser(r.data);
        fetchPhoto();
      })
      .catch(() => {
        localStorage.removeItem("bi_token");
        localStorage.removeItem("bi_user");
        localStorage.removeItem("bi_photo");
        setUser(null);
        setPhoto(null);
      })
      .finally(() => setLoading(false));
  }, [fetchPhoto]);

  const login = useCallback(async (email, name) => {
    const { data } = await api.post("/auth/demo-login", { email, name });
    localStorage.setItem("bi_token", data.token);
    localStorage.setItem("bi_user", JSON.stringify(data.user));
    setUser(data.user);
    fetchPhoto();
    return data.user;
  }, [fetchPhoto]);

  const logout = useCallback(() => {
    localStorage.removeItem("bi_token");
    localStorage.removeItem("bi_user");
    localStorage.removeItem("bi_photo");
    setUser(null);
    setPhoto(null);
  }, []);

  return (
    <AuthContext.Provider value={{ user, photo, login, logout, loading, refresh }}>
      {children}
    </AuthContext.Provider>
  );
};

export const useAuth = () => useContext(AuthContext);
